import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import moment from "moment";
import { IconCaretDownFilled } from "@tabler/icons-react";
import { DayPicker } from "react-day-picker";
import Button from "../../../../../components/button";
import { actionLabels, dayPickerModifiersStyles } from "../constants";

const Filters = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0px 0px 8px 0px #e1e2e6;
  padding: 16px;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 700px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    color: #555;
    font-family: "DM Sans", sans-serif;
    font-size: 0.85rem;
    font-weight: 500;
  }
`;

const SelectField = styled.div`
  position: relative;
`;

const SelectControl = styled.select`
  appearance: none;
  background-color: #fff;
  border: 1px solid #d9dde7;
  border-radius: 8px;
  box-sizing: border-box;
  color: #555;
  cursor: pointer;
  font-family: "DM Sans", sans-serif;
  font-size: 0.95rem;
  height: 40px;
  outline: none;
  padding: 0 36px 0 12px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  width: 100%;

  &:focus {
    border-color: ${(props) => props.theme.color.primary};
    box-shadow: 0 0 0 2px rgba(7, 12, 41, 0.08);
  }
`;

const DatePickerField = styled.div`
  position: relative;
`;

const DateButton = styled.button`
  align-items: center;
  appearance: none;
  background: #fff;
  border: 1px solid #d9dde7;
  border-radius: 8px;
  box-sizing: border-box;
  color: ${(props) => (props.$hasValue ? "#555" : "#8b91a1")};
  cursor: pointer;
  display: flex;
  font-family: "DM Sans", sans-serif;
  font-size: 0.95rem;
  height: 40px;
  justify-content: space-between;
  outline: none;
  padding: 0 12px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  width: 100%;

  &:focus {
    border-color: ${(props) => props.theme.color.primary};
    box-shadow: 0 0 0 2px rgba(7, 12, 41, 0.08);
  }
`;

const FilterCaretIcon = styled(IconCaretDownFilled)`
  color: #6c7488;
  flex-shrink: 0;
  height: 16px;
  width: 16px;
`;

const SelectCaretIcon = styled(FilterCaretIcon)`
  margin-top: -8px;
  pointer-events: none;
  position: absolute;
  right: 12px;
  top: 50%;
`;

const DateButtonIcon = styled(FilterCaretIcon)`
  margin-left: 8px;
`;

const DayPickerPopover = styled.div`
  background: #fff;
  border: 1px solid #d9dde7;
  border-radius: 12px;
  box-shadow: 0 14px 30px rgba(7, 12, 41, 0.12);
  left: 0;
  margin-top: 8px;
  padding: 12px;
  position: absolute;
  top: 100%;
  z-index: 60;

  .rdp {
    --rdp-accent-color: ${(props) => props.theme.color.primary};
    --rdp-background-color: #f3f5fb;
    margin: 0;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  align-items: flex-end;
`;

const ClearButton = styled(Button)`
  background: none;
  box-shadow: none;
  color: #888;
  margin: 0;

  &:hover {
    background: #efefef;
  }

  &:active {
    background: #ddd;
    box-shadow: none;
    color: #555;
  }
`;

const defaultFilters = {
  action_type: "",
  actor_id: "",
  date_from: "",
  date_to: ""
};

const LogFilters = ({ filters, actorOptions, onFiltersChange, onApply, onClear }) => {
  const [openDateField, setOpenDateField] = useState("");
  const dateFromRef = useRef(null);
  const dateToRef = useRef(null);

  useEffect(() => {
    if (!openDateField) return undefined;

    const handleMouseDown = (event) => {
      const activeRef = openDateField === "date_from" ? dateFromRef : dateToRef;
      if (activeRef.current && !activeRef.current.contains(event.target)) {
        setOpenDateField("");
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [openDateField]);

  const setFilterValue = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const getSelectedDay = (value) => {
    if (!value) return undefined;
    const parsedDay = new Date(`${value}T12:00:00`);
    return Number.isNaN(parsedDay.getTime()) ? undefined : parsedDay;
  };

  const getDateLabel = (value) => {
    if (!value) return "Select date";
    return moment(value, "YYYY-MM-DD").format("MMM D, YYYY");
  };

  const handleDateSelect = (key, day) => {
    setFilterValue(key, day ? moment(day).format("YYYY-MM-DD") : "");
    setOpenDateField("");
  };

  const handleApply = () => {
    setOpenDateField("");
    onApply();
  };

  const handleClear = () => {
    setOpenDateField("");
    onFiltersChange(defaultFilters);
    onClear();
  };

  return (
    <>
      <Filters>
        <FilterField>
          <label>Action</label>
          <SelectField>
            <SelectControl
              value={filters.action_type}
              onChange={(e) => setFilterValue("action_type", e.target.value)}
            >
              <option value="">All Actions</option>
              {Object.keys(actionLabels).map((key) => (
                <option key={key} value={key}>
                  {actionLabels[key]}
                </option>
              ))}
            </SelectControl>
            <SelectCaretIcon stroke={1.75} />
          </SelectField>
        </FilterField>
        <FilterField>
          <label>Trainer</label>
          <SelectField>
            <SelectControl
              value={filters.actor_id}
              onChange={(e) => setFilterValue("actor_id", e.target.value)}
            >
              <option value="">All Trainers</option>
              {actorOptions.map((actor) => (
                <option key={actor.id} value={actor.id}>
                  {actor.name || actor.email_address}
                </option>
              ))}
            </SelectControl>
            <SelectCaretIcon stroke={1.75} />
          </SelectField>
        </FilterField>
        <FilterField>
          <label>Date From</label>
          <DatePickerField ref={dateFromRef}>
            <DateButton
              type="button"
              $hasValue={Boolean(filters.date_from)}
              onClick={() => setOpenDateField((current) => (current === "date_from" ? "" : "date_from"))}
            >
              <span>{getDateLabel(filters.date_from)}</span>
              <DateButtonIcon stroke={1.75} />
            </DateButton>
            {openDateField === "date_from" && (
              <DayPickerPopover>
                <DayPicker
                  mode="single"
                  modifiersStyles={dayPickerModifiersStyles}
                  selected={getSelectedDay(filters.date_from)}
                  onSelect={(day) => handleDateSelect("date_from", day)}
                />
              </DayPickerPopover>
            )}
          </DatePickerField>
        </FilterField>
        <FilterField>
          <label>Date To</label>
          <DatePickerField ref={dateToRef}>
            <DateButton
              type="button"
              $hasValue={Boolean(filters.date_to)}
              onClick={() => setOpenDateField((current) => (current === "date_to" ? "" : "date_to"))}
            >
              <span>{getDateLabel(filters.date_to)}</span>
              <DateButtonIcon stroke={1.75} />
            </DateButton>
            {openDateField === "date_to" && (
              <DayPickerPopover>
                <DayPicker
                  mode="single"
                  modifiersStyles={dayPickerModifiersStyles}
                  selected={getSelectedDay(filters.date_to)}
                  onSelect={(day) => handleDateSelect("date_to", day)}
                />
              </DayPickerPopover>
            )}
          </DatePickerField>
        </FilterField>
        <Actions>
          <ClearButton label="Clear" onClick={handleClear} />
          <Button label="Apply" onClick={handleApply} style={{ marginTop: 0}} />
        </Actions>
      </Filters>
    </>
  );
};

export default LogFilters;
